import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AcoServices, IAco, NaoConformidades, Recomandacoes } from '../../../../../../services/api/action-plan/Aco';
import { Conformity, Recommendations, ToolsDetails } from '../../../../../../components/custom';
import { LayoutBase } from '../../../../../../layouts';
import { Box, Button, Divider, Grid, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CustomAlert } from '../../../../../../components/shared';
import React from 'react';
import { useAuthContext } from '../../../../../../context';
import { NavigateBefore, NavigateNext, Save } from '@mui/icons-material';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function tabProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const AcoDetails = () => {

    const { userData } = useAuthContext();
    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const url = useParams();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [valid, setValid] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.PAE?.conformidades;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    const [data, setData] = useState<IAco>({
        // uuid: null,
        id_barragem: '',
        empresa_avaliadora: '',
        data_rco: '',
        data_seminario: '',
        data_simulado: '',
        data_dco: '',
        status_dco: '',
        responsavel_tecnico: '',
        art: '',
        naoConformidades: [],
        recomendacoes: [],
    });

    useEffect(() => {
        setData({
            id_barragem: '',
            empresa_avaliadora: '',
            data_rco: '',
            data_seminario: '',
            data_simulado: '',
            data_dco: '',
            status_dco: '',
            responsavel_tecnico: '',
            art: '',
            naoConformidades: [],
            recomendacoes: [],
        });
        fetchData();
    }, [location.pathname]);

    function verifyForm() {
        return !!data.data_simulado && !!data.data_seminario && !!data.data_dco && !!data.data_rco;
    }

    const handleNew = () => {
        navigate(`/information_dam/${url.id}/semiannual_internal_training/detalhes/novo`);
    };

    const fetchData = () => {
        if (url.idAco) {
            AcoServices.get(url.idAco)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                    } else {
                        response.data_seminario = response.data_seminario ? new Date(response.data_seminario).toISOString().substring(0, 10) : '';
                        response.data_simulado = response.data_simulado ? new Date(response.data_simulado).toISOString().substring(0, 10) : '';
                        response.data_dco = response.data_dco ? new Date(response.data_dco).toISOString().substring(0, 10) : '';
                        response.data_rco = response.data_rco ? new Date(response.data_rco).toISOString().substring(0, 10) : '';
                        setData({
                            ...response
                        });
                    }
                });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const updatedData = { ...data, [name]: value };
        setData(updatedData);
        setValid(false);
    };

    const handleSave = () => {
        const isEditing = url.idAco !== 'novo';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');
        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error' });
            return;
        }
        setAlertInfo({ open: false, message: '', severity: 'success' });
        if (url.idAco == 'novo') {
            if (verifyForm()) {
                if (url.id) {
                    data.id_barragem = url.id;
                    AcoServices.create(data)
                        .then((response) => {
                            if (response instanceof Error) {
                                console.log(response.message);
                                setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                            } else {
                                console.log(response);
                                setAlertInfo({ open: true, message: 'ACO Criado', severity: 'success' });
                                setTimeout(() => {
                                    navigate(`/information_dam/${url.id}/aco`);
                                }, 2000);
                            }
                        });
                }
            } else {
                setValid(true);
            }
        } else {
            if (url.idAco !== undefined) {
                delete data.uuid;
                AcoServices.upgrade(data, url.idAco)
                    .then((response) => {
                        if (response instanceof Error) {
                            console.log(response.message);
                            setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                        } else {
                            setAlertInfo({ open: true, message: 'ACO Editado', severity: 'success' });
                            setTimeout(() => {
                                navigate(`/information_dam/${url.id}/aco`);
                            }, 2000);
                        }
                    });
            }
        }
    };

    const handleDelete = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar esta ação.', severity: 'error' });
            return;
        }
        if (url.idAco !== undefined && url.idAco !== 'novo') {
            AcoServices.delet(url.idAco)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                        setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });

                    } else {
                        setAlertInfo({ open: true, message: 'ACO excluido', severity: 'success' });
                    }
                });
        }
    };

    const handleRecommendation = (value: Recomandacoes[]) => {
        setData(prevData => { if (prevData) { return { ...prevData, recomendacoes: value }; } return { recomendacoes: value } as IAco; });
    };

    const handleComformity = (value: NaoConformidades[]) => {
        setData(prevData => { if (prevData) { return { ...prevData, naoConformidades: value }; } return { naoConformidades: value } as IAco; });
    };

    const handleChangeButtonNext = () => {
        if (value < 2) {
            setValue(value + 1);
        }
    };
    const handleChangeButtonBefor = () => {
        if (value > 0) {
            setValue(value - 1);
        }
    };

    return (
        <LayoutBase
            title='Cadastro ACO'
            tollBar={<ToolsDetails
                onClickSave={() => handleSave()}
                onClickNew={() => handleNew()}
                onClickDelet={() => handleDelete()}
                showDeletButton={hasActionPermission('remove')}
                showSaveButton={url.idAco == 'novo' ? hasActionPermission('add') : hasActionPermission('edit')}
                showNewButton={hasActionPermission('add')}
            />}
        >
            <Box padding={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
                    <Tabs value={value} onChange={handleChangeTab} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                        <Tab disabled sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Informações seminario" {...tabProps(0)} />
                        <Tab disabled sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Recomendações" {...tabProps(1)} />
                        <Tab disabled sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Conformidades" {...tabProps(2)} />
                    </Tabs>
                </Box>
                <Box padding={1} display={'flex'} justifyContent={'space-between'} flexDirection={smDown ? 'column' : 'row'} gap={smDown ? '5px' : 0}>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={<NavigateBefore />}
                        disabled={value == 0}
                        onClick={() => handleChangeButtonBefor()}
                    >Voltar</Button>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={value != 2 ? <NavigateNext /> : <Save />}
                        onClick={value != 2 ? () => handleChangeButtonNext() : () => handleSave()}
                        disabled={
                            (url.idAco == 'novo' && !hasActionPermission('add')) ||
                            (url.idAco !== 'novo' && !hasActionPermission('edit'))
                        }
                    >{value != 2 ? 'Proximo' : 'Salvar'}</Button>
                </Box>
                <CustomTabPanel value={value} index={0}>
                    <>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}
                            padding={1}
                        >
                            <Typography><strong>Informações do ACO</strong></Typography>
                        </Box>
                        <Divider />
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data simulado'
                                    type='date'
                                    name='data_simulado'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data.data_simulado}
                                    error={valid ? (data.data_simulado == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data seminario'
                                    type='date'
                                    name='data_seminario'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_seminario}
                                    error={valid ? (data.data_seminario == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Empresa avaliadora'
                                    type='text'
                                    name='empresa_avaliadora'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.empresa_avaliadora}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Responsável técnico'
                                    type='text'
                                    name='responsavel_tecnico'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.responsavel_tecnico}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Status DCO'
                                    type='text'
                                    name='status_dco'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.status_dco}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data DCO'
                                    type='date'
                                    name='data_dco'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_dco}
                                    error={valid ? (data.data_dco == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data RCO'
                                    type='date'
                                    name='data_rco'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_rco}
                                    error={valid ? (data.data_rco == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='ART'
                                    type='text'
                                    name='art'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.art}
                                />
                            </Grid>
                        </Grid>
                    </>
                </CustomTabPanel>

                <CustomTabPanel value={value} index={1}>
                    <Recommendations value={data.recomendacoes} onChange={handleRecommendation} smDown={smDown} />
                </CustomTabPanel>

                <CustomTabPanel value={value} index={2}>
                    <Conformity value={data.naoConformidades} onChange={handleComformity} smDown={smDown} />
                </CustomTabPanel>


            </Box>

            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};